/**
 * Safe raster preview helpers for decrypted / uploaded task attachments (APP-H03).
 * Never trust filename, extension, or declared MIME for active content.
 */

export const SAFE_RASTER_MIME = Object.freeze({
    png: 'image/png',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
});

const SAFE_UPLOAD_EXTENSIONS = new Set([
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.webp',
]);

export const isSafeRasterExtension = (filenameOrExt) => {
    const value = String(filenameOrExt || '').toLowerCase();
    const ext = value.startsWith('.')
        ? value
        : `.${value.split('.').pop() || ''}`;
    return SAFE_UPLOAD_EXTENSIONS.has(ext);
};

export const sniffSafeRasterMime = (bytes) => {
    if (!bytes || bytes.length < 12) {
        return null;
    }
    const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

    // PNG
    if (
        b[0] === 0x89 &&
        b[1] === 0x50 &&
        b[2] === 0x4e &&
        b[3] === 0x47 &&
        b[4] === 0x0d &&
        b[5] === 0x0a &&
        b[6] === 0x1a &&
        b[7] === 0x0a
    ) {
        return SAFE_RASTER_MIME.png;
    }

    // JPEG
    if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) {
        return SAFE_RASTER_MIME.jpeg;
    }

    // GIF
    if (
        b[0] === 0x47 &&
        b[1] === 0x49 &&
        b[2] === 0x46 &&
        b[3] === 0x38 &&
        (b[4] === 0x39 || b[4] === 0x37) &&
        b[5] === 0x61
    ) {
        return SAFE_RASTER_MIME.gif;
    }

    // WEBP (RIFF....WEBP)
    if (
        b[0] === 0x52 &&
        b[1] === 0x49 &&
        b[2] === 0x46 &&
        b[3] === 0x46 &&
        b[8] === 0x57 &&
        b[9] === 0x45 &&
        b[10] === 0x42 &&
        b[11] === 0x50
    ) {
        return SAFE_RASTER_MIME.webp;
    }

    // Reject obvious active markup (SVG/HTML) early.
    const head = new TextDecoder('utf-8', { fatal: false })
        .decode(b.slice(0, Math.min(64, b.length)))
        .trimStart()
        .toLowerCase();
    if (
        head.startsWith('<svg') ||
        head.startsWith('<?xml') ||
        head.startsWith('<!doctype html') ||
        head.startsWith('<html') ||
        head.startsWith('<script')
    ) {
        return null;
    }

    return null;
};

/**
 * Build a blob URL only for sniffed safe rasters. Declared MIME is ignored.
 */
export const createSafeRasterObjectUrl = (plainBytes, _declaredMime = '') => {
    const bytes =
        plainBytes instanceof Uint8Array
            ? plainBytes
            : new Uint8Array(plainBytes || []);
    const mime = sniffSafeRasterMime(bytes);
    if (!mime) {
        throw new Error(
            'Attachment is not a safe raster image (PNG/JPEG/GIF/WEBP required)',
        );
    }
    const blob = new Blob([bytes], { type: mime });
    return URL.createObjectURL(blob);
};

export const isSafeTaskSubmitImageFile = (file) => {
    if (!file) return false;
    const mime = String(file.type || '').toLowerCase();
    if (mime === 'image/svg+xml' || mime.includes('svg')) {
        return false;
    }
    if (mime && !Object.values(SAFE_RASTER_MIME).includes(mime) && mime !== 'image/jpg') {
        // Allow empty mime (some OS omit type) if extension is safe.
        if (mime.startsWith('image/') && mime !== 'image/jpg') {
            return false;
        }
    }
    if (!isSafeRasterExtension(file.name || '')) {
        return false;
    }
    return true;
};
